import React, { useEffect, useState } from 'react';
import { Pagination, Skeleton } from 'antd';
import {
  FlexWrapper,
  PaginationBox,
  PurpleText,
  SeeMore,
  TitleContainer,
  TitleH4
} from '../../../theme/common_style';
import { DeleteIcon, EditIcon, ReportNotFoundIcon } from '../../../theme/SvgIcons';
import EmptyData from '../../../components/common/EmptyData';
import styled from '@emotion/styled';
import dayjs from 'dayjs';
import CreateNotes from './CreateNotes';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import { deleteProjectRemarksApi, getProjectRemarksApi } from '../../../redux/project/apiRoute';
import { toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';

const Notes = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const location = useLocation();
  const { id } = location.state || {};

  const [seemore, setSeeMore] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: false
  });

  const handleAction = (data, type) => {
    if (type === 'delete') {
      setDeleteModal(true);
    } else {
      setOpen(true);
    }

    if (type === 'view') {
      setSeeMore(true);
    }
    setSelectedNote(data);
  };

  const handleClose = (type) => {
    if (type === 'delete') {
      setDeleteModal(false);
    } else {
      setOpen(false);
    }
    setSeeMore(false);
    setSelectedNote(null);
  };

  const description = (str) => {
    const length = str?.length;
    if (length > 100) {
      return str?.slice(0, 100).concat('...');
    } else {
      return str;
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      ...pagination,
      current: newPagination.current
    });
  };

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      let res = await deleteProjectRemarksApi(selectedNote?.id);
      if (res?.statusCode === 200) {
        getNotesData();
        setDeleteModal(false);
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (err) {
      toast.error(err?.message || 'Something went wrong');
    } finally {
      setDeleteLoading(false);
    }
  };

  const getNotesData = async () => {
    try {
      setLoading(true);
      let params = new URLSearchParams();
      params.append('limit', pagination?.pageSize);
      params.append('page', pagination?.current);
      let res = await getProjectRemarksApi(id, params);
      if (res?.statusCode === 200) {
        setData(res?.data?.remarks || []);
        setPagination({
          ...pagination,
          total: res?.data?.total
        });
        setLoading(false);
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    } catch (error) {
      toast.error(error?.message || 'Something went wrong');
    }
  };

  useEffect(() => {
    getNotesData();
  }, [id]);

  return (
    <>
      {open && (
        <CreateNotes
          open={open}
          onCancel={handleClose}
          edit={selectedNote}
          handleListing={getNotesData}
          seemore={seemore}
        />
      )}

      {deleteModal && (
        <ConfirmationModal
          open={deleteModal}
          onCancel={() => handleClose('delete')}
          title="Delete Note"
          onSubmit={handleDelete}
          buttonName="Delete"
          description={`Are you sure you want to delete ${selectedNote?.title} note?`}
          iconBG="#FB4A49"
          loading={deleteLoading}
        />
      )}

      {!loading && (
        <TitleContainer>
          <TitleH4>Notes</TitleH4>
          <PurpleText
            style={{ cursor: 'pointer', width: 'fit-content' }}
            onClick={() => setOpen(true)}>
            + Create New Note
          </PurpleText>
        </TitleContainer>
      )}
      {loading ? (
        <GridContainer>
          {Array.from({ length: 4 })?.map((_, index) => (
            <div
              key={index}
              style={{
                width: '100%',
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '16px'
              }}>
              <Skeleton active title={false} paragraph={{ rows: 5 }} />
            </div>
          ))}
        </GridContainer>
      ) : data?.length === 0 ? (
        <EmptyData
          height={'70vh'}
          icon={<ReportNotFoundIcon />}
          title={'Notes'}
          subTitle={'No Notes Found'}
        />
      ) : (
        <>
          <GridContainer>
            {data?.map((el, index) => (
              <NotesCard key={index}>
                <FlexWrapper width="100%" justify="space-between" align="center" wrap="no-wrap">
                  <div className="content">
                    <NoteTitle>{el?.title}</NoteTitle>
                    <DateTime>{dayjs(el?.created_at).format('DD MMM, YYYY, HH:mm A')}</DateTime>
                  </div>
                  <FlexWrapper gap="5px" wrap="no-wrap">
                    <div
                      className="icon"
                      style={{ background: '#65beee' }}
                      onClick={() => handleAction(el)}>
                      <EditIcon />
                    </div>
                    <div
                      className="icon"
                      style={{ background: '#fb4a49' }}
                      onClick={() => handleAction(el, 'delete')}>
                      <DeleteIcon />
                    </div>
                  </FlexWrapper>
                </FlexWrapper>
                <Description>{description(el?.remarks)}</Description>
                <SeeMore onClick={() => handleAction(el, 'view')}>See Full Details</SeeMore>
              </NotesCard>
            ))}
          </GridContainer>
          <PaginationBox>
            <Pagination pagination={pagination} hideOnSinglePage onChange={handleTableChange} />
          </PaginationBox>
        </>
      )}
    </>
  );
};

export default Notes;

export const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
`;

export const NotesCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  border-radius: 12px;
  background: #ffffff;

  .icon {
    cursor: pointer;
    width: 30px;
    height: 30px;
    opacity: 1;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

export const Description = styled.div`
  font-family: Plus Jakarta Sans;
  font-weight: 400;
  font-style: Regular;
  font-size: 14px;
  color: #767676;
  text-align: start;
  word-break: break-all;
`;

export const NoteTitle = styled.div`
  font-family: Plus Jakarta Sans;
  font-weight: 700;
  font-style: Bold;
  font-size: 16px;
  color: #0e0e0e;
  text-align: start;
`;

export const DateTime = styled.div`
  font-family: Plus Jakarta Sans;
  font-weight: 500;
  font-style: Medium;
  font-size: 14px;
  color: #65beee;
  text-align: start;
  text-align: start;
`;
